// React
import { memo, useCallback } from "react";

// UI
import { Button } from "ui/components/Button/Button";
import { Dropdown } from "ui/components/Dropdown/Dropdown";
import { NumberField } from "ui/components/TextField/NumberField";

// Tipovi i konstante
import { ADJUSTMENT_OPTIONS } from "ui/modules/pricing/components/PricingSchemeEditCard/consts";
import { SchemeItemRowProps } from "./types";

/**
 * Jedna stavka grupe.
 *
 * Fiksni iznos zamrzava cijenu zauvijek: kad se cjenik podigne, klijent ostaje na starom
 * iznosu i popust tiho naraste. Zato uz njega stoji upozorenje, a postotak je zadani izbor.
 */
const SchemeItemRow: React.FC<SchemeItemRowProps> = (props: SchemeItemRowProps) => {
  const { index, item, keyOptions, errors, onKeyChange, onTypeChange, onValueChange, onRemove } =
    props;

  const handleRemoveClick = useCallback(() => {
    onRemove(index);
  }, [index, onRemove]);

  const isAbsolute = item.adjustmentType === "Absolute";

  // Yup putanje za redak niza glase `items[0].keyCode`.
  const keyCodeError = errors[`items[${index}].keyCode`];
  const valueError = errors[`items[${index}].value`];
  const rowErrors = [keyCodeError, valueError].filter(Boolean);

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4">
      <div className="flex items-center justify-between gap-3 mb-3">
        <p className="text-sm font-semibold text-slate-600">Stavka {index + 1}</p>

        <Button variant="outlined" children="Ukloni" onClick={handleRemoveClick} />
      </div>

      <div className="flex flex-col md:flex-row gap-3 md:items-end">
        <div className="flex-1">
          <Dropdown
            name={`item-key-${index}`}
            label="Ključ"
            value={item.keyCode}
            options={keyOptions}
            onChange={onKeyChange}
            error={keyCodeError}
            width="full"
          />
        </div>

        <div className="flex-1">
          <Dropdown
            name={`item-type-${index}`}
            label="Tip"
            value={item.adjustmentType}
            options={ADJUSTMENT_OPTIONS}
            onChange={onTypeChange}
            width="full"
          />
        </div>

        <div className="w-[160px]">
          <NumberField
            name={`item-value-${index}`}
            label="Promjena"
            value={item.value}
            onChange={onValueChange}
            error={valueError}
            groupAppend={isAbsolute ? "€" : "%"}
            decimals={isAbsolute ? 2 : 2}
            allowNegative
            size="full"
          />
        </div>
      </div>

      {isAbsolute && (
        <p className="text-sm text-amber-700 mt-3">
          Fiksni iznos zamrzava cijenu zauvijek. Ako se cjenik kasnije podigne, klijent ostaje na
          starom iznosu i popust tiho naraste.
        </p>
      )}

      {/* Polja greške pokazuju samo kroz tooltip, pa se ovdje ispisuje i tekst. */}
      {rowErrors.length > 0 && (
        <p className="text-sm text-red-600 mt-2">{rowErrors.join(". ")}</p>
      )}
    </div>
  );
};

export default memo(SchemeItemRow);
